import { useContractRead } from 'wagmi';
import { useChainId } from 'wagmi';
import { formatUnits } from 'viem';
import { getContracts, getFeeConfig, isDynamicFeesEnabled } from '../config/contracts';

// FeeManager ABI for reading dynamic fees
const FEE_MANAGER_ABI = [
  {
    "inputs": [],
    "name": "getChatFeeInLEXI",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "getFactSubmissionFeeInLEXI",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "getVotingFeeInLEXI",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "getAllFeesInLEXI",
    "outputs": [
      {"internalType": "uint256", "name": "chatFee", "type": "uint256"},
      {"internalType": "uint256", "name": "factSubmissionFee", "type": "uint256"},
      {"internalType": "uint256", "name": "votingFee", "type": "uint256"},
      {"internalType": "uint256", "name": "currentPrice", "type": "uint256"}
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "feeConfig",
    "outputs": [
      {"internalType": "uint256", "name": "chatFeeUSD", "type": "uint256"},
      {"internalType": "uint256", "name": "factSubmissionUSD", "type": "uint256"},
      {"internalType": "uint256", "name": "votingUSD", "type": "uint256"},
      {"internalType": "bool", "name": "isActive", "type": "bool"}
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "getLEXIPriceUSD",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  }
] as const;

export interface FeeInfo {
  chatFee: string;
  factSubmissionFee: string;
  votingFee: string;
  minStake: string;
  isDynamic: boolean;
  isLoading: boolean;
  error?: string;
}

export function useDynamicFees(): FeeInfo {
  const chainId = useChainId();
  const contracts = getContracts(chainId);
  const feeConfig = getFeeConfig(chainId);
  const isDynamic = isDynamicFeesEnabled(chainId);

  // Read dynamic fees from FeeManager contract (mainnet only)
  const { data: dynamicFees, isLoading: feesLoading, error: feesError } = useContractRead({
    address: contracts.FEE_MANAGER as `0x${string}`,
    abi: FEE_MANAGER_ABI,
    functionName: 'getAllFeesInLEXI',
    query: {
      enabled: isDynamic && contracts.FEE_MANAGER !== '0x0000000000000000000000000000000000000000',
    },
  });

  // For testnet or when FeeManager is not available, use static config
  if (!isDynamic || !contracts.FEE_MANAGER || contracts.FEE_MANAGER === '0x0000000000000000000000000000000000000000') {
    return {
      chatFee: feeConfig.chatFee.toString(),
      factSubmissionFee: feeConfig.factSubmissionFee.toString(),
      votingFee: feeConfig.votingFee.toString(),
      minStake: feeConfig.minStake,
      isDynamic: false,
      isLoading: false,
    };
  }

  // For mainnet with dynamic fees
  if (feesLoading) {
    return {
      chatFee: '0',
      factSubmissionFee: '0',
      votingFee: '0',
      minStake: feeConfig.minStake,
      isDynamic: true,
      isLoading: true,
    };
  }

  // Return fallback if there's an error or no data
  if (feesError || !dynamicFees) {
    return {
      chatFee: feeConfig.chatFee.toString(),
      factSubmissionFee: feeConfig.factSubmissionFee.toString(),
      votingFee: feeConfig.votingFee.toString(),
      minStake: feeConfig.minStake,
      isDynamic: true,
      isLoading: false,
      error: feesError?.message || 'Failed to fetch dynamic fees',
    };
  }

  // Convert from wei to readable format
  // Check if dynamicFees is valid and has expected structure

  // Handle both array and object responses
  let chatFee: bigint | undefined;
  let factSubmissionFee: bigint | undefined;
  let votingFee: bigint | undefined;
  let currentPrice: bigint | undefined;

  if (Array.isArray(dynamicFees)) {
    if (dynamicFees.length < 4) {
      console.warn('Invalid dynamicFees array length, using fallback:', dynamicFees);
      return {
        chatFee: feeConfig.chatFee.toString(),
        factSubmissionFee: feeConfig.factSubmissionFee.toString(),
        votingFee: feeConfig.votingFee.toString(),
        minStake: feeConfig.minStake,
        isDynamic: true,
        isLoading: false,
        error: 'Invalid fee data structure',
      };
    }
    [chatFee, factSubmissionFee, votingFee, currentPrice] = dynamicFees as [bigint, bigint, bigint, bigint];
  } else {
    // Handle object response
    const fees = dynamicFees as any;
    chatFee = fees.chatFee || fees[0];
    factSubmissionFee = fees.factSubmissionFee || fees[1];
    votingFee = fees.votingFee || fees[2];
    currentPrice = fees.currentPrice || fees[3];
  }

  // Validate all values are defined and are bigint before formatting
  if (chatFee === undefined || factSubmissionFee === undefined || votingFee === undefined || currentPrice === undefined ||
      typeof chatFee !== 'bigint' || typeof factSubmissionFee !== 'bigint' || typeof votingFee !== 'bigint' || typeof currentPrice !== 'bigint') {
    console.warn('Missing or invalid fee values, using fallback:', { chatFee, factSubmissionFee, votingFee, currentPrice });
    return {
      chatFee: feeConfig.chatFee.toString(),
      factSubmissionFee: feeConfig.factSubmissionFee.toString(),
      votingFee: feeConfig.votingFee.toString(),
      minStake: feeConfig.minStake,
      isDynamic: true,
      isLoading: false,
      error: 'Missing or invalid fee values',
    };
  }

  // Log for debugging
  try {
    console.log('FeeManager Debug:', {
      chatFee: formatUnits(chatFee, 18),
      factSubmissionFee: formatUnits(factSubmissionFee, 18),
      votingFee: formatUnits(votingFee, 18),
      currentPriceUSD: formatUnits(currentPrice, 8), // Price has 8 decimals
    });
  } catch (e) {
    console.warn('Error formatting fee debug info:', e);
  }

  try {
    return {
      chatFee: formatUnits(chatFee, 18),
      factSubmissionFee: formatUnits(factSubmissionFee, 18),
      votingFee: formatUnits(votingFee, 18),
      minStake: feeConfig.minStake, // Fallback value if contract read fails
      isDynamic: true,
      isLoading: false,
    };
  } catch (e) {
    console.error('Error formatting fees:', e);
    return {
      chatFee: feeConfig.chatFee.toString(),
      factSubmissionFee: feeConfig.factSubmissionFee.toString(),
      votingFee: feeConfig.votingFee.toString(),
      minStake: feeConfig.minStake,
      isDynamic: true,
      isLoading: false,
      error: 'Error formatting fees',
    };
  }
}

export function useChatFee(): { fee: string; isLoading: boolean; error?: string } {
  const chainId = useChainId();
  const contracts = getContracts(chainId);
  const isDynamic = isDynamicFeesEnabled(chainId);

  const { data: chatFee, isLoading, error } = useContractRead({
    address: contracts.FEE_MANAGER as `0x${string}`,
    abi: FEE_MANAGER_ABI,
    functionName: 'getChatFeeInLEXI',
    query: {
      enabled: isDynamic && contracts.FEE_MANAGER !== '0x0000000000000000000000000000000000000000',
    },
  });

  if (!isDynamic || !contracts.FEE_MANAGER || contracts.FEE_MANAGER === '0x0000000000000000000000000000000000000000') {
    const feeConfig = getFeeConfig(chainId);
    return {
      fee: feeConfig.chatFee.toString(),
      isLoading: false,
    };
  }

  return {
    fee: chatFee ? formatUnits(chatFee, 18) : '0',
    isLoading,
    error: error?.message,
  };
}

export function useFactSubmissionFee(): { fee: string; isLoading: boolean; error?: string } {
  const chainId = useChainId();
  const contracts = getContracts(chainId);
  const isDynamic = isDynamicFeesEnabled(chainId);

  const { data: factFee, isLoading, error } = useContractRead({
    address: contracts.FEE_MANAGER as `0x${string}`,
    abi: FEE_MANAGER_ABI,
    functionName: 'getFactSubmissionFeeInLEXI',
    query: {
      enabled: isDynamic && contracts.FEE_MANAGER !== '0x0000000000000000000000000000000000000000',
    },
  });

  if (!isDynamic || !contracts.FEE_MANAGER || contracts.FEE_MANAGER === '0x0000000000000000000000000000000000000000') {
    const feeConfig = getFeeConfig(chainId);
    return {
      fee: feeConfig.factSubmissionFee.toString(),
      isLoading: false,
    };
  }

  return {
    fee: factFee ? formatUnits(factFee, 18) : '0',
    isLoading,
    error: error?.message,
  };
}

export function useVotingFee(): { fee: string; isLoading: boolean; error?: string } {
  const chainId = useChainId();
  const contracts = getContracts(chainId);
  const isDynamic = isDynamicFeesEnabled(chainId);

  const { data: votingFee, isLoading, error } = useContractRead({
    address: contracts.FEE_MANAGER as `0x${string}`,
    abi: FEE_MANAGER_ABI,
    functionName: 'getVotingFeeInLEXI',
    query: {
      enabled: isDynamic && contracts.FEE_MANAGER !== '0x0000000000000000000000000000000000000000',
    },
  });

  if (!isDynamic || !contracts.FEE_MANAGER || contracts.FEE_MANAGER === '0x0000000000000000000000000000000000000000') {
    const feeConfig = getFeeConfig(chainId);
    return {
      fee: feeConfig.votingFee.toString(),
      isLoading: false,
    };
  }

  return {
    fee: votingFee ? formatUnits(votingFee, 18) : '0',
    isLoading,
    error: error?.message,
  };
}
